import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginPrincipalService, AppLoginSubjectService, IdentityUserProfileService, Pagination, TenantService, UserAppletService, UserAppletTenantLoginEPService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { UserPermSearchModel } from '../../../../../models/advanced-search-models/user-permissions-listing-search.model';
import { UserModel } from '../../../../../models/user-permissions.model';
import { gridOptionsServerSide, pageSorting, pageFiltering, serverSideClear } from '../../../../../listing.utils';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { forkJoin, iif, Observable, of } from 'rxjs';
import { filter, map, mergeMap, shareReplay, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../permission-controller/root';

import { UserPermActions } from '../../../user-permissions-controller/actions';
import { UserPermSelectors } from '../../../user-permissions-controller/selectors';
import { UserPermStates } from '../../../user-permissions-controller/states';
import { SearchQueryModel } from '../../../../../models/query.model';
import { TranslateService } from "@ngx-translate/core";
interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-user-permission-listing',
  templateUrl: './user-permission-listing.component.html',
  styleUrls: ['./user-permission-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})

export class UserPermissionListingComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 0;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  toggleColumn$: Observable<boolean>;
  searchModel = UserPermSearchModel;

  // api visa
  apiVisa = AppConfig.apiVisa;
  masterVisa = AppConfig.masterVisa;

  // initial grid state
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'single';

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  appletGuid = sessionStorage.getItem('appletGuid');
  tenantCode = sessionStorage.getItem('tenantCode');
  tenantGuid: string;
  currentUserGuid: string;
  appletCatalogGuids: string[];

  //translation variable
  email_text: string;
  mobile_text: string;
  rank_text: string;

  constructor(
    private identityUserProfileService: IdentityUserProfileService,
    // private userAppletService: UserAppletService,
    private userAppletService: UserAppletTenantLoginEPService,
    // private appLoginPrincipalService: AppLoginPrincipalService,
    private appLoginSubjectService: AppLoginSubjectService,
    private tenantService: TenantService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private readonly componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.email_text = res.email;
        this.mobile_text = res.mobile;
        this.rank_text = res.rank;
      });
    } else {
      // Fallback if translation service is unavailable
      this.email_text = 'Email';
      this.mobile_text = 'Mobile';
      this.rank_text = 'Rank';
    }
  }

  /**
   * Lifecycle method: called when component is first initialised.
   */
   ngOnInit(): void {
    // initial configurations for toggle column and local state
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });

    // initialise column definitions
    this.columnsDefs = [
      {
        headerName: this.email_text,
        field: 'main_email',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.mobile_text,
        field: 'main_phone',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.rank_text,
        field: 'userModel.bl_applet_login_subject_link.rank',
        cellStyle: {textAlign: 'left'}
      }
    ];

    // get info. about current tenant guid and current user
    this.subSink.sink = this.getTenant()
    .subscribe(x => {
      if (!this.tenantGuid) {
        this.tenantGuid = x.data[0].app_tenant_hdr.guid.toString();
      }
      this.getCurrentUser();
    });
  }

  /**
   * Gets information of the current user that is currently logged in.
   */
   getCurrentUser() {
    const currentUser$ = this.getOwnProfile();
    this.subSink.sink = currentUser$
    .subscribe((user) => {
      const newUser: UserModel = new UserModel();
      newUser.bl_applet_login_subject_link = user.link.bl_applet_login_subject_link;
      user.profile['appLoginPrincipalList'].forEach(x => {
        if (x.principal_type == 'MOBILE_USERNAME') {
          newUser.app_login_principal_mobile = x
        } else {
          newUser.app_login_principal_email = x
        }
      })
      // save current user in store
      this.store.dispatch(UserPermActions.setCurrentUser({currentUser: newUser}));
    })
  }

  /**
   * Returns an object containing the current info. of the current user in the profile
   * field and the applet-login-subject link of this user to this applet.
   *
   * @returns an observable containing an object
   * {profile: AppLoginContainerModelUpdate, link: UserAppletContainerModel}
   */
  getOwnProfile() {
    return this.identityUserProfileService.get(this.apiVisa)
    .pipe(
      map(x => {this.currentUserGuid = x.data['appLoginSubject']['guid'].toString(); return x.data}),
      mergeMap(x => {
        const appletLoginSubjectLink$ = this.getAppletLoginSubjectLink(this.currentUserGuid);
        return forkJoin(appletLoginSubjectLink$)
        .pipe(map(([link]) => {return {profile: x, link: link}}))
      }),
      map(x => x)
    )
  }


  /**
   * Return an observable containing the UserAppletContainerModel whose subject guid
   * matches the guid given in the parameters and is related to this applet.
   *
   * @param guid : the guid of the applet-login-subject-link subject
   * @returns an observable containing the UserAppletContainerModel
   */
  getAppletLoginSubjectLink(subjectGuid) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_login_guids', operator: '=', value: subjectGuid},
      {columnName: 'applet_hdr_guid', operator: '=', value: this.appletGuid},
      {columnName: 'tenant_guid', operator: '=', value: this.tenantGuid}
    ]
    const pagination = new Pagination(null, 1, criteria);

    return this.userAppletService.getByGuidCriteria(pagination, this.apiVisa, this.appletGuid.toString())
    .pipe(
      map(x => x.data
        .filter(x => x.bl_applet_login_subject_link.applet_hdr_guid == this.appletGuid)
        [0])
    )
  }

  /**
   * Get the current tenant's guid and set the variable tenantGuid.
   */
  getTenant() {
    const criteria = [
      {columnName: 'code', operator: '=', value: this.tenantCode}
    ]
    return this.tenantService.getByCriteria(new Pagination(null, null, criteria), this.apiVisa)
      .pipe(
        shareReplay(1)
      )
      // .subscribe(x => {
      //   const tenant = x.data[0];
      //   this.tenantGuid = tenant.app_tenant_hdr.guid.toString();
      // })
  }

  /**
   * This function is called when AG grid is first initialised and initially
   * set the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.getTenant()
    .subscribe(x => {
      if (!this.tenantGuid) {
        this.tenantGuid = x.data[0].app_tenant_hdr.guid.toString();
      }
      const datasource = this.getDatasource();
      this.gridApi.setServerSideDatasource(datasource);
    })

    // refresh AG grid if there has been an update
    this.store.select(UserPermSelectors.selectUpdateUserListing).subscribe(x => {
      if (x) {
        this.gridApi.refreshServerSideStore();
        this.store.dispatch(UserPermActions.setUpdateUserListing({update: false}));
      }
    })

  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    return {
      getRows: grid => {

        // dispatch action for loading users
        this.store.dispatch(UserPermActions.loadUsersInit({request: grid.request}));
        if (!conditionalCriteria) {
          conditionalCriteria = [
            {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
            {columnName: 'applet_hdr_guid', operator: '=', value: this.appletGuid},
            {columnName: 'tenant_guid', operator: '=', value: this.tenantGuid}
          ]
        }

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, conditionalCriteria);

        this.subSink.sink = this.userAppletService.getByGuidCriteria(pagination, this.apiVisa, this.appletGuid.toString())
          .pipe(
            mergeMap(a => {
              const source: Observable<UserModel>[] = [];
              a.data.forEach((b) => {
                const userModel: UserModel = new UserModel();
                userModel.bl_applet_login_subject_link = b.bl_applet_login_subject_link
                // get the user
                const result$ = this.getUser(b.bl_applet_login_subject_link.app_login_guid.toString()).pipe(
                  map((userFromApi) => {
                    // Combine the userModel with the userFromApi data
                    return { ...userFromApi, userModel };
                  })
                );

                  // .pipe(
                  //   map(users => {

                  //     users.forEach(user => {
                  //       const isMobile = user.app_login_principal.principal_type == 'MOBILE_USERNAME' ? true : false;
                  //       if (isMobile) {
                  //         userModel.app_login_principal_mobile = user.app_login_principal;
                  //       } else {
                  //         userModel.app_login_principal_email = user.app_login_principal;
                  //       }
                  //     })

                  //     return userModel
                  //   })
                  // );
                // push the UserModel observable into the array
                source.push(result$);
              });
              return iif(() => source.length > 0,
                forkJoin(source).pipe(map(x => {
                  return {...a,
                  data: x}
                })),
                of({...a, data: [], totalRecords: 0}))
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;

            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }
            console.log("DEBUG>>>>>>", data)
            grid.successCallback(data, totalRecords);
            this.store.dispatch(UserPermActions.loadUsersSuccess({totalRecords: a.totalRecords}));
          }, err => {
            grid.failCallback();
            this.store.dispatch(UserPermActions.loadUsersFailure({error: err.message}));
          })
      }
    }

  }

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
  getUser(subjectGuid: string) {
    // initialise new pagination
    const pagination = new Pagination();
    // add necessary criteria
    pagination.conditionalCriteria.push({
      columnName: 'guids',
      operator: '=',
      value: subjectGuid
    });

    return this.appLoginSubjectService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map((subject) => {
          return subject.data[0]})
      )

  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
   onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  // /**
  //  * This function is called every time the user clicks on a row in the
  //  * AG grid.
  //  *
  //  * @param entity : the entity on the AG grid that was clicked on
  //  */
   onRowClicked(entity) {
    // dispatch ngrx action
    this.store.dispatch(UserPermActions.selectUser({user: entity}));

    // deactivate add and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: false,
          deactivateList: true,
        });
      // trigger the next view column
      this.viewColFacade.onNext(1);
    }
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
   /**
   * Runs the search bar's query against the user-permissions listing's own
   * endpoint.
   *
   * Replaces the raw SQL that <app-advanced-search> built from
   * UserPermSearchModel and posted to SubQueryService (a SELECT over
   * bl_applet_login_subject_link joined to app_login_principal, with the applet
   * and tenant scoping appended here). The search bar is now
   * <app-advanced-search-general>, which emits the keyword and the form value
   * instead of SQL.
   *
   * GET applet-login-subject-link/applet-login-ep/{applet}/query ->
   * AppletLoginSubjectLinkUow.generateWhereClauseByCriteria joins
   * app_login_principal itself and honours `search_word` (ILIKE over
   * principal_id, principal_value and rank), `rank`, `applet_hdr_guid` and
   * `tenant_guid`.
   *
   * getDatasource only applies its defaults when called with no argument, so the
   * calcTotalRecords / applet_hdr_guid / tenant_guid scoping is re-seeded here --
   * otherwise a search would widen the listing beyond this applet and tenant.
   *
   * The search also moves off masterVisa onto apiVisa: it is no longer a call to
   * the separate sub-query service, it is the same request the listing already
   * makes.
   */
  onSearch(e: SearchQueryModel) {
    const datasource = this.getDatasource(this.buildSearchCriteria(e));
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the emitted search, or undefined when it is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria(e: SearchQueryModel) {
    if (!e || e.isEmpty) {
      return undefined;
    }
    const scope = [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'applet_hdr_guid', operator: '=', value: this.appletGuid },
      { columnName: 'tenant_guid', operator: '=', value: this.tenantGuid }
    ];
    if (e.keyword) {
      return scope.concat([
        { columnName: 'search_word', operator: '=', value: e.keyword.trim() }
      ]);
    }
    const query: any = e.queryString || {};
    return scope.concat(
      [{ columnName: 'rank', operator: '=', value: query.rank }]
        .filter(condition => condition.value)
    );
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
